import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Save } from 'lucide-react';

import { ConsignmentEditForm, SrfUserEntryContext, type ConsignmentEditFormRef } from '@/widgets/consignment-edit-form';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/shared/ui/action-button';

import { useConsignmentCreate } from '../lib';
import { SrfImportErrorsAlert } from './srf-import-errors-alert';

export function ConsignmentCreatePage() {
  const { t } = useTranslation();
  const formRef = useRef<ConsignmentEditFormRef>(null);
  const {
    handleSave,
    handleBack,
    prefillValues,
    prefillLabels,
    srfUserEntry,
    isSrfLoading,
    importedConsignment,
    importValidationErrors,
    importFieldErrors,
    fieldHints,
    dismissImportAlert
  } = useConsignmentCreate();

  const handleSubmit = () => {
    formRef.current?.submit();
  };

  return (
    <div className="pb-20 sm:pb-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:sticky sm:-top-4 md:-top-6 sm:-mt-4 md:-mt-6 sm:z-20 sm:-mx-4 sm:px-4 md:-mx-6 md:px-6 sm:py-3 sm:border-b sm:bg-background">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label={t('gen.back.label')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">{t('newcmr.create.title', 'Create New Consignment')}</h1>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-background p-4 sm:static sm:z-auto sm:ml-auto sm:border-0 sm:bg-transparent sm:p-0">
          <ActionButton intent="primary" size="sm" className="w-full sm:w-auto" onClick={handleSubmit}>
            <Save className="h-4 w-4 mr-2" />
            {t('newcmr.create.label', 'Save New Consignment')}
          </ActionButton>
        </div>
      </div>

      <SrfImportErrorsAlert errors={importValidationErrors} onDismiss={dismissImportAlert} />

      <SrfUserEntryContext.Provider value={srfUserEntry}>
        <ConsignmentEditForm
          ref={formRef}
          mode="create"
          consignmentData={importedConsignment}
          prefillValues={prefillValues}
          prefillLabels={prefillLabels}
          serverFieldErrors={importFieldErrors}
          serverFieldHints={fieldHints}
          isLoading={isSrfLoading}
          onSave={handleSave}
        />
      </SrfUserEntryContext.Provider>
    </div>
  );
}
